<template>
  <Transition name="slide-panel">
    <div v-if="visible"
      class="fixed right-0 top-0 h-full z-[200] flex flex-col"
      style="width: 280px; max-width: 90vw;"
    >
      <!-- Backdrop for mobile -->
      <div 
        class="fixed inset-0 bg-black/20 z-[-1] md:hidden"
        @click="$emit('close')"
      />

      <!-- Panel -->
      <div class="h-full flex flex-col bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 sticky top-0 z-10">
          <div>
            <h3 class="text-base font-bold text-gray-900 dark:text-white">{{ $t('chords.title') }}</h3>
            <div class="flex items-center gap-2 mt-1">
              <select
                v-model="instrument"
                @change="saveInstrument"
                class="text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-gray-700 dark:text-gray-200 focus:outline-none"
              >
                <option value="guitar">Guitar</option>
                <option value="ukulele">Ukulele</option>
                <option value="piano">Piano</option>
              </select>
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {{ chords.length }} {{ chords.length === 1 ? $t('chords.single') : $t('chords.plural') }}
              </span>
            </div>
          </div>
          <button 
            @click="$emit('close')" 
            class="p-1.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Chord list -->
        <div class="flex-1 overflow-y-auto py-3 px-2" id="chord-diagrams-scrollable">
          <div v-if="!chords.length" class="text-center text-gray-400 dark:text-gray-500 mt-16 px-4">
            <svg class="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <p class="text-sm">{{ $t('chords.noChordsFound') }}</p>
          </div>

          <!-- Two-column grid of chord cards -->
          <div class="grid grid-cols-2 gap-2">
            <div
              v-for="chord in mappedChords"
              :key="chord.original"
              :id="'chord-card-' + chord.original.replace(/[^a-zA-Z0-9]/g, '-')"
              class="chord-card rounded-xl border-2 p-2 transition-all duration-200 cursor-pointer flex flex-col items-center select-none"
              :class="[
                highlightedChord === chord.original
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-md shadow-indigo-200 dark:shadow-indigo-900/30'
                  : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10'
              ]"
              @click="cycleVariation(chord)"
            >
              <template v-if="chord.data">
                <PianoDiagram
                  v-if="instrument === 'piano'"
                  :chordName="chord.original"
                  :position="chord.data.positions[chord.currentVariation]"
                />
                <ChordDiagram
                  v-else
                  :chordName="chord.original"
                  :position="chord.data.positions[chord.currentVariation]"
                  :currentVariation="chord.currentVariation"
                  :totalVariations="chord.data.positions.length"
                  :highlighted="highlightedChord === chord.original"
                />
              </template>
              <div v-else class="py-4 flex flex-col items-center gap-1 w-full">
                <span class="text-lg font-bold text-gray-900 dark:text-white">{{ chord.original }}</span>
                <span class="text-[10px] text-gray-400 dark:text-gray-500">{{ $t('chords.noDiagram') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer tip -->
        <div class="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-slate-800">
          <p class="text-[10px] text-gray-400 dark:text-gray-500 text-center">{{ $t('chords.clickToCycle') }}</p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits } from 'vue'
import ChordDiagram from './ChordDiagram.vue'
import PianoDiagram from './PianoDiagram.vue'
import guitarDb from '@tombatossals/chords-db/lib/guitar.json'
import ukuleleDb from '@tombatossals/chords-db/lib/ukulele.json'
import pianoDb from './piano.json'

const props = defineProps({
  visible: { type: Boolean, default: false },
  chords: { type: Array, default: () => [] },
  highlightedChord: { type: String, default: null }
})

defineEmits(['close'])

const variations = ref({})
const instrument = ref(localStorage.getItem('mytriad-chord-instrument') || 'guitar')

const saveInstrument = () => {
  localStorage.setItem('mytriad-chord-instrument', instrument.value)
  variations.value = {} // Reset selected variations
}

const currentDb = computed(() => {
  if (instrument.value === 'piano') return pianoDb
  return instrument.value === 'ukulele' ? ukuleleDb : guitarDb
})

// Reset variations when chord list changes
watch(() => props.chords, () => {
  variations.value = {}
}, { deep: true })

// Auto-scroll to highlighted chord
watch(() => props.highlightedChord, async (newVal) => {
  if (newVal && props.visible) {
    await nextTick()
    const safeId = 'chord-card-' + newVal.replace(/[^a-zA-Z0-9]/g, '-')
    const el = document.getElementById(safeId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }
})

// Parse a chord string into { key, suffix } matching chords-db format.
const parseChord = (chordString) => {
  // Split into root-part and optional bass-part on "/"
  const slashIdx = chordString.indexOf('/')
  const hasBass = slashIdx !== -1
  const rootPart = hasBass ? chordString.slice(0, slashIdx) : chordString
  const bassPart = hasBass ? chordString.slice(slashIdx) : '' // e.g. "/E" or "/C#"

  const match = rootPart.match(/^([A-G][#b]?)(.*)$/)
  if (!match) return null

  const key = match[1]
  let qualityPart = match[2] // e.g. "" for C, "m" for Am, "m7" for Am7

  // Normalize common chord quality shorthand aliases
  if (qualityPart === 'm') qualityPart = 'minor'
  else if (qualityPart === 'M' || qualityPart === 'maj') qualityPart = 'major'
  else if (qualityPart === '°' || qualityPart === 'dim') qualityPart = 'dim'
  else if (qualityPart === '°7' || qualityPart === 'o7' || qualityPart === 'dim7') qualityPart = 'dim7'
  else if (qualityPart === 'sus' || qualityPart === '4') qualityPart = 'sus4'
  else if (qualityPart === '2') qualityPart = 'sus2'
  else if (qualityPart === '+' || qualityPart === 'aug') qualityPart = 'aug'
  else if (qualityPart === '7#5') qualityPart = 'aug7'
  else if (qualityPart === '7+' || qualityPart === '7M' || qualityPart === 'M7' || qualityPart === 'Δ7' || qualityPart === 'Δ' || qualityPart === 'maj7') qualityPart = 'maj7'
  else if (qualityPart === 'ø' || qualityPart === 'm7b5') qualityPart = 'm7b5'

  // Build the suffix the same way the db stores it
  let suffix
  if (hasBass) {
    let dbQuality = qualityPart
    if (dbQuality === 'major') dbQuality = ''
    else if (dbQuality === 'minor') dbQuality = 'm'
    suffix = dbQuality + bassPart
  } else {
    suffix = qualityPart || 'major'
  }

  return { key, suffix }
}

// The chords-db object uses "Csharp" and "Fsharp" as object keys (not "C#"/"F#")
const keyMap = {
  'C#': 'Csharp', 'Db': 'Csharp',
  'D#': 'Eb',
  'F#': 'Fsharp', 'Gb': 'Fsharp',
  'G#': 'Ab',
  'A#': 'Bb'
}

const findChordData = (chordString) => {
  const parsed = parseChord(chordString)
  if (!parsed) return null
  const key = keyMap[parsed.key] || parsed.key
  const keyData = currentDb.value.chords[key]
  if (!keyData) return null

  let suffix = parsed.suffix
  if (instrument.value === 'piano' && suffix === 'minor') {
    suffix = 'm'
  }

  // 1. Try exact suffix match (covers "/E", "m/C#", "m7" etc.)
  const exact = keyData.find(c => c.suffix === suffix)
  if (exact) return exact

  // 2. Fallback for slash chords: try the root quality alone
  if (suffix.includes('/')) {
    const rootQuality = suffix.split('/')[0]
    const fallbackSuffix = rootQuality === '' ? 'major' : (rootQuality === 'm' || rootQuality === 'minor') ? (instrument.value === 'piano' ? 'm' : 'minor') : rootQuality
    const rootMatch = keyData.find(c => c.suffix === fallbackSuffix)
    if (rootMatch) return rootMatch
  }

  // 3. Final fallback: first chord entry for the key (only for plain major chords)
  return suffix === 'major' ? keyData[0] : null
}

const mappedChords = computed(() => {
  return props.chords.map(c => {
    const data = findChordData(c)
    if (variations.value[c] === undefined) {
      variations.value[c] = 0
    }
    return {
      original: c,
      data,
      currentVariation: variations.value[c]
    }
  })
})

const cycleVariation = (chord) => {
  if (!chord.data || chord.data.positions.length <= 1) return
  const current = variations.value[chord.original] || 0
  variations.value[chord.original] = (current + 1) % chord.data.positions.length
}
</script>

<style scoped>
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-panel-enter-from,
.slide-panel-leave-to {
  transform: translateX(100%);
}
</style>
